/**
 * Hook for querying frozen item state — count of frozen items and
 * grace-period preview list of items that will be frozen.
 */
import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';
import { toast } from 'sonner';
import { mapSupabaseError } from '@/lib/errorMapper';

/**
 * Returns the count of the current user's frozen items.
 * Only queries when the user is on free tier (frozen items can't exist for paid users).
 */
export function useFrozenItemCount() {
  const { user } = useAuth();

  return useQuery({
    queryKey: ['frozen-item-count', user?.id],
    queryFn: async () => {
      if (!user) return 0;

      const { count, error } = await supabase
        .from('items')
        .select('*', { count: 'exact', head: true })
        .eq('owner_id', user.id)
        .eq('is_frozen', true);

      if (error) throw error;
      return count ?? 0;
    },
    enabled: !!user,
  });
}

export interface FreezeCandidateItem {
  id: string;
  name: string;
}

/**
 * Returns items that will be frozen when the grace period ends.
 * Only fetches when `isInGracePeriod` is true.
 *
 * [LEARN] Uses the `calculate_freeze_candidates` RPC so that the items shown here
 * match exactly what the downgrade notification previewed and what the cron will
 * actually freeze — one source of truth for ordering.
 */
export function useGraceFreezeCandidates(isInGracePeriod: boolean) {
  const { user } = useAuth();

  return useQuery({
    queryKey: ['grace-freeze-candidates', user?.id],
    queryFn: async (): Promise<FreezeCandidateItem[]> => {
      if (!user) return [];

      const { data: candidates, error } = await supabase.rpc(
        'calculate_freeze_candidates',
        { p_user_id: user.id }
      );

      if (error) {
        toast.error(mapSupabaseError(error));
        return [];
      }

      const toFreezeIds = (candidates ?? [])
        .filter((c: { item_id: string; will_freeze: boolean }) => c.will_freeze)
        .map((c: { item_id: string }) => c.item_id);

      if (toFreezeIds.length === 0) return [];

      const { data: items, error: itemsError } = await supabase
        .from('items')
        .select('id, name')
        .in('id', toFreezeIds);

      if (itemsError) throw itemsError;

      return (items ?? []).map((i) => ({ id: i.id, name: i.name }));
    },
    enabled: !!user && isInGracePeriod,
    staleTime: 5 * 60 * 1000, // 5 min — recalculates if loans change
  });
}
